import { api } from "../../../app/api";
import { PAYSLIPS_PAGE_SIZE } from "../../../common/components/functional/pagination/constants";
import { Hits, Relation } from "../../../common/types/Paginated";
import { Options } from "../../../common/types/requestOptions";

export interface PayslipEvent {
  id: string;
  name: string;
  version: number;
  payload: any;
}

export interface PayslipTotals {
  // tyel
  pensionInsurance: string;
  // tvm
  unemploymentInsurance: string;
  // ennakonpidätys
  taxWithholding: string;
  // veroton
  taxExempt: string;
  // ennakonpidätyksen alainen
  taxable: string;
  // sos. turv. päiväraha
  healthInsurancePerDiem: string;
  // sos. turv. m. alainen osuus (työnantajan sairasvakuutusmaksu)
  employerHealthInsurance: string;
  // luontoisedut
  fringeBenefits: string;
  // rahapalkka
  incomeGross: string;
  // maksetaan
  incomeNet: string;
}

export interface Payslip {
  id: string;
  isManual: boolean;
  payslipNumber: number;
  version: number;
  talentId: string;
  eezyCompanyId: string;
  periodStart: string;
  periodEnd: string;
  valueDate: string;
  salaryDate: string;
  incomeRegisterId: string | null;
  payer: {
    name: string;
  };
  recipient: {
    name: string;
    ssn?: string;
  };
  subtotalsPeriod: PayslipTotals;
  status: {
    code: string;
  };
}

export interface PayslipRow {
  employmentId: string;
  title: string;
  rows: {
    expenseTypeCode: string;
    expense: string;
    amount: string;
    unit: string;
    unitCost: string;
    description: string;
    timeRanges: {
      start: number;
      end: number;
    }[];
  }[];
}

export interface PayslipDetailed {
  id: string;
  payslipNumber: number;
  version: number;
  talentId: string;
  eezyCompanyId: string;
  periodStart: string;
  periodEnd: string;
  valueDate: string;
  salaryDate: string;
  payer: {
    name: string;
  };
  recipient: {
    name: string;
    ssn?: string;
    iban: string;
    bic: string;
    withholdingRate?: string;
    additionalWithholdingRate?: string;
    incomeCeiling?: string;
    incomeGross?: string;
    taxable?: string;
    taxWithholding?: string;
  };
  rows: PayslipRow[];
  subtotalsPeriod: PayslipTotals;
  subtotalsYear: PayslipTotals;
  employeeHealthInsuranceRate?: string;
}

export interface PayslipsResponse {
  hits: Hits;
  data: Payslip[];
  cursor: string | null;
  totals: PayslipTotals;
}

export interface PayslipsMoreResponse {
  data: Payslip[];
  cursor: string | null;
}

export interface PayslipFilters {
  cursor: string | null;
  filters: {
    ssn?: string;
    talentIds?: string[];
    eezyCompanyIds?: string[];
    payslipNumbers?: string[];
    statusCodes?: string[];
    salaryTypes?: string[];
    periodStart?: string;
    periodEnd?: string;
    valueDateFrom?: string;
    valueDateTo?: string;
    salaryDateFrom?: string;
    salaryDateTo?: string;
    incomeNetFrom?: string;
    incomeNetTo?: string;
    salaryPeriodClassId?: string;
    pageSize?: string;
  };
}

export interface PayslipDetailedParams {
  id: string;
  languageCode: string;
}

export interface ManualPayslipPayload {
  talentId: string;
  eezyCompanyId: string;
  periodStart: string;
  periodEnd: string;
  valueDate: string;
  salaryDate: string;
}

export interface UpdatePayslipPayload {
  payslipId: string;
  valueDate?: string;
  salaryDate?: string;
}

export const emptyPayslip: Payslip = {
  id: "",
  isManual: false,
  payslipNumber: 0,
  version: 0,
  talentId: "",
  eezyCompanyId: "",
  periodStart: "",
  periodEnd: "",
  valueDate: "",
  salaryDate: "",
  payer: { name: "" },
  incomeRegisterId: null,
  recipient: {
    name: "",
  },
  status: {
    code: "",
  },
  subtotalsPeriod: {
    pensionInsurance: "",
    unemploymentInsurance: "",
    taxWithholding: "",
    taxExempt: "",
    taxable: "",
    healthInsurancePerDiem: "",
    employerHealthInsurance: "",
    fringeBenefits: "",
    incomeGross: "",
    incomeNet: "",
  },
};

export interface InvalidateExpensesPayload {
  talentId: string;
  date: string;
  onlyAccumulations: boolean;
}

export const getPayslipById = (
  params: PayslipDetailedParams,
  { requestId }: Options = {},
) => {
  return api.get<PayslipDetailed>("/projections/qry/payslipById", {
    requestId,
    auth: true,
    queryParams: {
      ...params,
    },
  });
};

export const getPayslips = (
  filters: PayslipFilters,
  { requestId }: Options = {},
) => {
  const filterObject = filters.filters;
  let params: Record<string, string> = {};
  for (const [key, value] of Object.entries(filterObject)) {
    if (value.toString()) {
      params[key] = value.toString();
    }
  }
  // Don't send fetch if there are no filters
  if (Object.keys(params).length === 0) {
    const emptyResponse: PayslipsResponse = {
      hits: { value: 0, relation: Relation.DEFAULT },
      data: [],
      cursor: null,
      totals: {
        pensionInsurance: "0",
        unemploymentInsurance: "0",
        taxWithholding: "0",
        taxExempt: "0",
        taxable: "0",
        healthInsurancePerDiem: "0",
        employerHealthInsurance: "0",
        fringeBenefits: "0",
        incomeGross: "0",
        incomeNet: "0",
      },
    };
    return emptyResponse;
  }

  return api
    .get<PayslipsResponse>("/projections/qry/payslips", {
      requestId,
      auth: true,
      queryParams: {
        ...params,
        totals: "true",
        pageSize: PAYSLIPS_PAGE_SIZE,
      },
    })
    .then((response) => {
      return response.data;
    });
};

export const getMorePayslips = (
  filters: PayslipFilters,
  { requestId }: Options = {},
) => {
  const filterObject = { ...filters.filters, cursor: filters.cursor };
  let params: Record<string, string> = {};
  for (const [key, value] of Object.entries(filterObject)) {
    if (value?.toString()) {
      params[key] = value.toString();
    }
  }
  if (Object.keys(params).length === 0) {
    return {
      data: { hits: { value: 0, relation: "eq" }, data: [], cursor: null },
    };
  }

  return api.get<PayslipsMoreResponse>("/projections/qry/payslips", {
    requestId,
    auth: true,
    queryParams: {
      ...params,
      pageSize: PAYSLIPS_PAGE_SIZE,
    },
  });
};

export const approvePayslip = (
  payslipId: string,
  { requestId }: Options = {},
) => {
  return api.post<PayslipEvent[]>("/invoicing/cmd/approvePayslip", {
    requestId,
    auth: true,
    body: {
      payslipId,
      taxCardIsRequired: false,
    },
  });
};

export const unapprovePayslip = (
  payslipId: string,
  { requestId }: Options = {},
) => {
  return api.post<PayslipEvent[]>("/invoicing/cmd/unapprovePayslip", {
    requestId,
    auth: true,
    body: {
      payslipId,
    },
  });
};

export const lockPayslip = (payslipId: string, { requestId }: Options = {}) => {
  return api.post<PayslipEvent[]>("/invoicing/cmd/lockPayslip", {
    requestId,
    auth: true,
    body: {
      payslipId,
    },
  });
};

export const deletePayslip = (
  payslipId: string,
  { requestId }: Options = {},
) => {
  return api.post<PayslipEvent[]>("/invoicing/cmd/deletePayslip", {
    requestId,
    auth: true,
    body: {
      payslipId,
    },
  });
};

export const generateManualPayslip = (
  payload: ManualPayslipPayload,
  { requestId }: Options = {},
) => {
  return api.post<PayslipEvent[]>("/invoicing/cmd/generateManualPayslip", {
    requestId,
    auth: true,
    body: {
      ...payload,
    },
  });
};

export const savePayslipChanges = (
  payload: UpdatePayslipPayload,
  { requestId }: Options = {},
) => {
  return api.post<PayslipEvent[]>("/invoicing/cmd/updatePayslip", {
    requestId,
    auth: true,
    body: {
      ...payload,
    },
  });
};

export const invalidateExpenses = (
  payload: InvalidateExpensesPayload,
  { requestId }: Options = {},
) => {
  return api.post<any>("/working/cmd/invalidateExpenses", {
    requestId,
    auth: true,
    body: {
      ...payload,
    },
  });
};

export const generateSalaryPayment = (
  payload: { payslipId: string },
  { requestId }: Options = {},
) => {
  return api.post<PayslipEvent[]>("/invoicing/cmd/createManualSalaryPayment", {
    requestId,
    auth: true,
    body: {
      ...payload,
    },
  });
};
